import React from 'react';
import { InView as ReactIntersectionObserver, IntersectionObserverProps } from 'react-intersection-observer';

import { withBrowser } from '../Responsive/WithBrowser';
import { withSidebarContext } from '../SidebarContext/WithSidebarContext';

type Props = Pick<IntersectionObserverProps, Exclude<keyof IntersectionObserverProps, 'onChange'>> & {
  mobile?: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  disableSidebarWindowEvents: () => void;
  enableSidebarWindowEvents: () => void;
  ignoreTriggerTill: number;
};

class VerticalMenuTrigger extends React.PureComponent<Props, { triggerCount: number }> {

  static defaultProps = { ignoreTriggerTill: 0 };

  private inView = true;

  readonly state = { triggerCount: 0 };

  componentDidUpdate(prevProps: Readonly<Props>) {
    // exit if isMobileView doesn't change
    if (prevProps.mobile === this.props.mobile) return;

    // inView and transitioned from moible to not mobile
    if (this.inView && prevProps.mobile && !this.props.mobile) {
      this.props.openSidebar();
    }

    // inView and transitioned from not moible to mobile
    if (this.inView && !prevProps.mobile && this.props.mobile) {
      this.props.closeSidebar();
    }

    if (this.inView) {
      this.props.disableSidebarWindowEvents();
    } else {
      this.props.enableSidebarWindowEvents();
    }
  }

  componentDidMount() {
    if (this.props.mobile) this.props.closeSidebar();
  }

  componentWillUnmount() {
    this.props.enableSidebarWindowEvents();
  }

  readonly onChange = (inView: boolean) => {
    this.setState({ triggerCount: this.state.triggerCount + 1 });

    if (this.state.triggerCount < this.props.ignoreTriggerTill) return;

    this.inView = inView;

    if (inView) {
      this.props.disableSidebarWindowEvents();
    } else {
      this.props.enableSidebarWindowEvents();
    }

    if (this.props.mobile) return;

    if (inView) {
      this.props.openSidebar();
    } else {
      this.props.closeSidebar();
    }
  };

  render() {
    const { children, ...props } = this.props;

    const fn = children;

    if (typeof fn !== 'function') return null;

    return (
      <ReactIntersectionObserver onChange={this.onChange} {...props}>
        {props => fn(props)}
      </ReactIntersectionObserver>
    );
  }
}

const Browsered = withBrowser(browser => ({ mobile: browser.is.mobile }))(VerticalMenuTrigger);

const SidebarContexted = withSidebarContext(({
  open,
  close,
  enableWindowEvents,
  disableWindowEvents,
}) => ({
  openSidebar: open,
  closeSidebar: close,
  enableSidebarWindowEvents: enableWindowEvents,
  disableSidebarWindowEvents: disableWindowEvents,
}))(Browsered);
type SidebarContexted = typeof SidebarContexted;

export {
  SidebarContexted as VerticalMenuTrigger,
  VerticalMenuTrigger as _VerticalMenuTrigger,
  Props as _VerticalMenuTriggerProps,
};
